import React from 'react';
import momentZ from 'moment-timezone';
import sizeMe from 'react-sizeme';
import {
  VictoryAxis, VictoryBar, VictoryChart, VictoryGroup, VictoryLegend, VictoryLine, VictoryScatter, VictoryTooltip,
} from 'victory';

export const RefundSaverChartType = {LINES: 'lines', BARS: 'bars'};

const tooltip = <VictoryTooltip
  pointerWidth={20}
  style={{fontSize: 11, fill: '#FFFFFF', fontFamily: 'sans-serif'}}
  flyoutStyle={{strokeWidth: 1, fill: '#545454'}}
/>;

const formatBucket = (date, group) => {
  if (group === 'month') {
    return momentZ(date).format('MMM YYYY');
  }
  if (group === 'week') {
    return `wk ${momentZ(date).format('DD MMM')}`;
  }
  return momentZ(date).format('DD MMM');
};

// series: [{name, color, data: [{x: Date, y: number, bucket: ISO}]}]; clicking a point / bar calls
// onSelect(bucket) — the cases of that bucket are listed under the charts.
const RefundSaverChart = ({
  series, chartType, group, yLabel, formatY = (v) => v, onSelect, selectedBucket, size, height = 280,
}) => {
  const dates = ((series[0] && series[0].data) || []).map((point) => point.x);
  const every = Math.max(1, Math.ceil(dates.length / 12));
  const tickValues = dates.filter((_, index) => index % every === 0);
  const clickEvents = onSelect ? [{
    target: 'data',
    eventHandlers: {onClick: (e, props) => {
      onSelect(props.datum.bucket);
      return [];
    }},
  }] : [];
  const label = (name) => ({datum}) => `${name}: ${formatY(datum.y)}\n${formatBucket(datum.x, group)}`;
  const isSelected = (datum) => selectedBucket && datum.bucket === selectedBucket;

  const renderBars = () => <VictoryGroup offset={Math.max(4, Math.min(18, (size.width - 100) / Math.max(dates.length, 1) / (series.length + 1)))}>
    {series.map((s) => <VictoryBar
      key={s.name}
      data={s.data}
      barWidth={Math.max(3, Math.min(16, (size.width - 100) / Math.max(dates.length, 1) / (series.length + 1)))}
      labels={label(s.name)}
      labelComponent={tooltip}
      events={clickEvents}
      style={{data: {fill: ({datum}) => (isSelected(datum) ? '#1f2d3d' : s.color), cursor: onSelect ? 'pointer' : 'default'}}}
    />)}
  </VictoryGroup>;

  const renderLines = () => series.flatMap((s) => [
    <VictoryLine key={`${s.name}-line`} data={s.data} style={{data: {stroke: s.color, strokeWidth: 2}}}/>,
    <VictoryScatter
      key={`${s.name}-points`}
      data={s.data}
      size={({datum}) => (isSelected(datum) ? 6 : 3.5)}
      labels={label(s.name)}
      labelComponent={tooltip}
      events={clickEvents}
      style={{data: {fill: s.color, cursor: onSelect ? 'pointer' : 'default'}}}
    />,
  ]);

  return <div>
    <VictoryLegend
      orientation="horizontal"
      gutter={20}
      width={size.width}
      height={24}
      padding={{top: 4, bottom: 0, left: 50}}
      data={series.map((s) => ({name: s.name, symbol: {fill: s.color}}))}
    />
    <VictoryChart
      scale={{x: 'time'}}
      width={size.width || 600}
      height={height}
      padding={{top: 10, bottom: 40, left: 60, right: 20}}
      domainPadding={{x: chartType === RefundSaverChartType.BARS ? 30 : 10, y: [0, 10]}}
    >
      <VictoryAxis
        tickValues={tickValues}
        tickFormat={(t) => formatBucket(t, group)}
        style={{tickLabels: {fontSize: 9, padding: 4}, axis: {stroke: '#756f6a'}}}
      />
      <VictoryAxis
        dependentAxis
        label={yLabel}
        tickFormat={(t) => formatY(t)}
        style={{axisLabel: {fontSize: 11, padding: 45}, tickLabels: {fontSize: 9, padding: 2}, grid: {stroke: '#ececec'}}}
      />
      {chartType === RefundSaverChartType.BARS ? renderBars() : renderLines()}
    </VictoryChart>
  </div>;
};

export default sizeMe()(RefundSaverChart);
